import {
  FileSpreadsheetIcon,
  CopyIcon,
  MailIcon,
  UnplugIcon,
  UsersIcon,
  LayoutDashboardIcon,
  CalendarRangeIcon,
  CalculatorIcon,
  WorkflowIcon,
  MessagesSquareIcon,
  UserRoundIcon,
  ServerIcon,
  FileCodeIcon,
  EyeOffIcon,
  CalendarXIcon,
  StickyNoteIcon,
  UserPlusIcon,
  ListTodoIcon,
  ShieldCheckIcon,
  KeyRoundIcon,
  MonitorIcon,
  AppleIcon,
  SmartphoneIcon,
  TabletIcon,
} from 'lucide-react'
import type { IconTextItem } from '../types/content'
import type { FAQItem } from './faq'

// Kleuraccenten voor iconen op de webapplicatiepagina. Lichte tint met
// donkere tekst, zodat het contrast overal AA haalt.
export const webappAccents: { chip: string; tint: string }[] = [
  { chip: 'bg-[#E6F0FF] text-[#004AC7]', tint: 'bg-[#EEF5FA]' },
  { chip: 'bg-emerald-100 text-emerald-700', tint: 'bg-emerald-50' },
  { chip: 'bg-amber-100 text-amber-700', tint: 'bg-amber-50' },
  { chip: 'bg-violet-100 text-violet-700', tint: 'bg-violet-50' },
  { chip: 'bg-rose-100 text-rose-700', tint: 'bg-rose-50' },
  { chip: 'bg-sky-100 text-sky-700', tint: 'bg-sky-50' },
]

// Platforms waarop de applicatie te installeren is.
export const webappPlatforms: { label: string; icon: IconTextItem['icon'] }[] = [
  { label: 'Windows', icon: MonitorIcon },
  { label: 'Mac', icon: AppleIcon },
  { label: 'iPhone en iPad', icon: TabletIcon },
  { label: 'Android', icon: SmartphoneIcon },
]

// Wat een "echte app" betekent voor de gebruiker.
export const webappAppPoints: string[] = [
  'Een eigen icoon op je bureaublad of startscherm, net als een app uit de App Store of Play Store.',
  'Opent in een eigen venster, zonder adresbalk of browsertabbladen. Je hebt niet het gevoel dat je op een website zit.',
  'Snel en overzichtelijk, op je computer op kantoor en op je telefoon onderweg.',
]

// Functies die in veel van onze applicaties terugkomen.
export const webappFeatures: IconTextItem[] = [
  {
    title: 'Taken en to-do\u2019s met je hele team',
    body: 'Zet taken klaar, wijs ze toe aan een collega en zie in een oogopslag wat af is en wat nog openstaat. Geen appgroepen of briefjes meer.',
    icon: ListTodoIcon,
  },
  {
    title: 'Rollen en rechten',
    body: 'Jij bepaalt wie wat ziet. Een monteur ziet zijn eigen klussen, kantoor ziet de planning en alleen de directie ziet de cijfers.',
    icon: KeyRoundIcon,
  },
  {
    title: 'Meerdere gebruikers',
    body: 'Iedereen logt in met een eigen account. Zo weet je wie wat heeft aangepast, en haal je iemand er met een klik weer af.',
    icon: UsersIcon,
  },
  {
    title: 'Veilig, op een sterke fundering',
    body: 'Een versleutelde verbinding, een eigen login per persoon en rechten per rol. We bouwen op bewezen, veelgebruikte techniek in plaats van op een experiment.',
    icon: ShieldCheckIcon,
  },
]

// Een greep uit bekende pakketten die een koppeling bieden. Wat er precies
// kan verschilt per pakket; dat staat ook zo in de tekst op de pagina.
export const webappIntegrations: string[] = [
  'Google Agenda',
  'Apple Agenda',
  'Outlook en Microsoft 365',
  'Google Workspace',
  'Exact Online',
  'Moneybird',
  'SnelStart',
  'e-Boekhouden',
  'Mollie',
  'HubSpot',
  'Mailchimp',
]

// Signalen dat standaardsoftware of Excel niet meer volstaat.
export const webappSignals: IconTextItem[] = [
  {
    title: 'Je werkt in vijf Excel-bestanden tegelijk',
    body: 'Planning in het ene bestand, klanten in het andere, prijzen in een derde. En alleen jij weet welke versie de juiste is.',
    icon: FileSpreadsheetIcon,
  },
  {
    title: 'Je voert dezelfde gegevens twee keer in',
    body: 'Een klant komt binnen via de mail, gaat in je planning, daarna in je offerte en daarna in je boekhouding. Vier keer overtypen.',
    icon: CopyIcon,
  },
  {
    title: 'Klanten mailen om te vragen hoe het ervoor staat',
    body: 'Elke statusvraag kost jou een telefoontje of een mail. Terwijl de informatie er al is, alleen niet op een plek waar de klant kan kijken.',
    icon: MailIcon,
  },
  {
    title: 'Je tools praten niet met elkaar',
    body: 'Je agenda, je CRM en je boekhouding doen elk hun werk, maar niets gaat automatisch van het ene naar het andere.',
    icon: UnplugIcon,
  },
  {
    title: 'Taken lopen via WhatsApp en briefjes',
    body: 'Wie wat doet staat in een appgroep, op een post-it of in iemands hoofd. Is die persoon ziek, dan weet niemand waar het werk staat.',
    icon: StickyNoteIcon,
  },
  {
    title: 'Afspraken staan in drie verschillende agenda\u2019s',
    body: 'De planning op kantoor klopt niet met de agenda op de telefoon van je medewerker. Dubbele afspraken en gemiste bezoeken zijn het gevolg.',
    icon: CalendarXIcon,
  },
  {
    title: 'Iedereen ziet alles, of juist niets',
    body: 'In een gedeelde map kan iedereen overal bij, ook bij gegevens die niet voor hen bedoeld zijn. Of je deelt niets, en dan loopt alles via jou.',
    icon: EyeOffIcon,
  },
  {
    title: 'Een nieuwe collega inwerken kost veel tijd',
    body: 'Omdat elke taak op een andere plek staat, moet je bij elke nieuwe medewerker opnieuw uitleggen waar alles staat en hoe het werkt.',
    icon: UserPlusIcon,
  },
]

// Wat we bouwen. Voorbeelden van soorten toepassingen, geen klantnamen.
export const webappExamples: IconTextItem[] = [
  {
    title: 'Klantportaal',
    body: 'Een omgeving waar je klanten zelf de status van hun opdracht zien, documenten uploaden of een afspraak inplannen.',
    icon: UsersIcon,
  },
  {
    title: 'Intern dashboard',
    body: 'Eén overzicht van wat er loopt, wat er openstaat en wie waarmee bezig is. Opgebouwd uit de gegevens die je al hebt.',
    icon: LayoutDashboardIcon,
  },
  {
    title: 'Planning en roosters',
    body: 'Een planning die past bij hoe jouw bedrijf werkt, met de regels en uitzonderingen die een standaardpakket niet kent.',
    icon: CalendarRangeIcon,
  },
  {
    title: 'Offerte- en calculatietool',
    body: 'Je rekenregels op één plek, zodat een offerte in minuten klaar is en iedereen in je team met dezelfde prijzen rekent.',
    icon: CalculatorIcon,
  },
  {
    title: 'Koppelingen tussen systemen',
    body: 'Je boekhouding, CRM en agenda laten samenwerken, zodat gegevens één keer worden ingevoerd en daarna vanzelf doorlopen.',
    icon: WorkflowIcon,
  },
  {
    title: 'Samenwerkingstool',
    body: 'Een gedeelde werkplek voor je team, je partners of je klanten, waarin trajecten samen worden opgepakt en niets tussen wal en schip valt.',
    icon: MessagesSquareIcon,
  },
]

// Werkwijze in volgorde. De volgorde doet ertoe, dus we tonen stappen.
export const webappSteps: { title: string; body: string }[] = [
  {
    title: 'Kennismaking',
    body: 'We praten over je bedrijf en over waar het nu wringt. Nog niet over techniek, wel over hoe het werk loopt en waar tijd verloren gaat.',
  },
  {
    title: 'Wensen uitwerken',
    body: 'We zetten op papier wat de applicatie moet doen, voor wie, en wat er in de eerste versie zit. Op basis daarvan krijg je een vaste offerte.',
  },
  {
    title: 'Klikbaar ontwerp',
    body: 'Voordat er code geschreven wordt, klik je door een ontwerp van de belangrijkste schermen. Zo zie je vroeg of het klopt met hoe jij werkt.',
  },
  {
    title: 'Bouwen in korte rondes',
    body: 'We bouwen in rondes van een paar weken en laten na elke ronde zien wat er staat. Je stuurt bij terwijl het nog makkelijk kan.',
  },
  {
    title: 'Livegang',
    body: 'We zetten de applicatie live, helpen met het overzetten van bestaande gegevens en leggen je team uit hoe het werkt.',
  },
  {
    title: 'Beheer en doorontwikkeling',
    body: 'Na de livegang houden we de applicatie veilig en up-to-date. Komen er nieuwe wensen bij, dan bouwen we die er in overleg bij.',
  },
]

// "Van jou, niet van een platform". Formulering sluit aan op artikel 17 van
// de algemene voorwaarden: gebruiksrecht na betaling, overdracht van
// broncode alleen als dat is afgesproken.
export const webappOwnershipPoints: IconTextItem[] = [
  {
    title: 'Geen licentie per gebruiker',
    body: 'Je betaalt niet elke maand per medewerker. Neem je iemand aan, dan krijgt die gewoon een account.',
    icon: UserRoundIcon,
  },
  {
    title: 'Gebouwd rond jouw werkwijze',
    body: 'Je past je proces niet aan een pakket aan. De applicatie volgt hoe jouw bedrijf werkt, en groeit mee als dat verandert.',
    icon: WorkflowIcon,
  },
  {
    title: 'Hosting en beheer op één adres',
    body: 'We kunnen de applicatie hosten en onderhouden op onze eigen omgeving. Dan heb je één aanspreekpunt voor alles.',
    icon: ServerIcon,
  },
  {
    title: 'Afspraken over de broncode vooraf',
    body: 'Wil je de broncode zelf in bezit hebben, bijvoorbeeld om later met een andere partij verder te kunnen? Dat leggen we vast in de offerte.',
    icon: FileCodeIcon,
  },
]

export const webappFaqs: FAQItem[] = [
  {
    question: 'Wat kost een webapplicatie ongeveer?',
    answer:
      'Dat hangt af van de omvang: hoeveel schermen, hoeveel soorten gebruikers en met welke systemen de applicatie moet koppelen. Een eenvoudige calculatietool is een ander project dan een klantportaal met koppelingen naar je boekhouding. Daarom noemen we vooraf geen bedrag. Na een kennismaking werken we je wensen uit en krijg je een vaste offerte, zodat je vooraf weet waar je aan toe bent.',
  },
  {
    question: 'Hoe lang duurt het om een webapp te laten maken?',
    answer:
      'Ook dat hangt af van de omvang. We beginnen met een eerste versie die het belangrijkste probleem oplost en bouwen die in korte rondes. Na elke ronde zie je wat er staat. In de offerte staat een planning, zodat je weet wanneer je wat kunt verwachten.',
  },
  {
    question: 'Wie is eigenaar van de code?',
    answer:
      'Na volledige betaling krijg je het recht om de applicatie voor je eigen bedrijfsvoering te gebruiken, zonder licentiekosten per gebruiker. Standaard blijft de broncode bij ons, zoals ook in onze algemene voorwaarden staat. Wil je de broncode zelf ontvangen, dan spreken we dat vooraf af en leggen we het vast in de offerte.',
  },
  {
    question: 'Kan de applicatie koppelen met mijn boekhouding?',
    answer:
      'Vaak wel. Veel boekhoudpakketten, CRM-systemen en agenda\u2019s bieden een koppeling waarmee gegevens heen en weer kunnen. Welke gegevens precies en hoe ver dat gaat, verschilt per pakket. In de fase waarin we je wensen uitwerken zoeken we dat voor jouw situatie uit, voordat er iets gebouwd wordt.',
  },
  {
    question: 'Kan ik de app op mijn computer en telefoon installeren?',
    answer:
      'Ja. De applicatie is te installeren op Windows, Mac, iPhone, iPad en Android. Je krijgt een eigen icoon op je bureaublad of startscherm en de app opent in een eigen venster, zonder adresbalk. Zo werkt het als een gewone app. Of een publicatie in de App Store of Play Store nodig is, bespreken we per project.',
  },
  {
    question: 'Wat gebeurt er na de livegang?',
    answer:
      'Een applicatie heeft onderhoud nodig: beveiligingsupdates, controle of koppelingen blijven werken en af en toe een aanpassing. Dat kunnen we voor je doen, samen met de hosting. Komen er nieuwe wensen bij, dan bespreken we die en bouwen we ze er in overleg bij.',
  },
]
